// src/firebase/user.service.js
import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    query,
    setDoc,
    updateDoc,
    serverTimestamp,
    where,
} from "firebase/firestore";
import { db } from "./firebase.config";

const normalizeName = (value) => {
    if (typeof value !== 'string') return '';
    return value.trim().replace(/\s+/g, ' ');
};

const deriveFirstName = (value) => {
    const normalized = normalizeName(value);
    if (!normalized) return '';
    const [first = ''] = normalized.split(' ');
    return first;
};

const logProvider = (event, payload = {}) => {
    console.info("[auth.singleProvider]", {
        event,
        timestamp: new Date().toISOString(),
        ...payload,
    });
};

export const createUserProfile = async (user, additionalData = {}) => {
    if (!user) return;

    const userRef = doc(db, "users", user.uid);

    // Prevent overwriting existing documents
    const existing = await getDoc(userRef);
    if (existing.exists()) {
        throw new Error("User profile already exists. Cannot overwrite.");
    }

    const fullName = normalizeName(additionalData.fullName || user.displayName || "");

    const userProfile = {
        uid: user.uid,
        email: (user.email || "").toLowerCase(),
        fullName,
        firstName: deriveFirstName(fullName),
        phone: additionalData.phone || "",
        companyName: additionalData.companyName || "",
        role: additionalData.role || "",
        photoURL: additionalData.photoURL || user.photoURL || "",
        provider: additionalData.provider || "password",
        onboardingCompleted: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    };

    await setDoc(userRef, userProfile);
};

export const getUserProfile = async (userId) => {
    if (!userId) return null;
    const userRef = doc(db, "users", userId);
    const snap = await getDoc(userRef);
    return snap.exists() ? snap.data() : null;
};

export const getUserProfileByEmail = async (email) => {
    if (!email) return null;

    const usersRef = collection(db, "users");
    const byEmailQuery = query(usersRef, where("email", "==", email), limit(1));
    const snap = await getDocs(byEmailQuery);

    if (snap.empty) return null;

    const profileDoc = snap.docs[0];
    return {
        id: profileDoc.id,
        data: profileDoc.data(),
        ref: profileDoc.ref,
    };
};

export const updateUserProfile = async (userId, data) => {
    if (!userId) throw new Error("User ID is required");

    const {
        uid: _uid,
        email: _email,
        provider: _provider,
        createdAt: _createdAt,
        onboardingCompleted: _onboarding,
        ...safeData
    } = data;

    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
        ...safeData,
        updatedAt: serverTimestamp(),
    });
};

export const handleGoogleSignIn = async (firebaseUser) => {
    if (!firebaseUser?.uid) throw new Error("No Google user provided");

    const userRef = doc(db, "users", firebaseUser.uid);
    const snap = await getDoc(userRef);

    logProvider("firestore_profile_check", {
        uid: firebaseUser.uid,
        email: firebaseUser.email || null,
        firestoreExists: snap.exists(),
    });

    if (snap.exists()) {
        const profile = snap.data();
        const profileFullName = normalizeName(profile.fullName || profile.name || '');
        const googleDisplayName = normalizeName(firebaseUser.displayName || '');

        const updates = { updatedAt: serverTimestamp() };

        if (profile.provider !== "google") {
            updates.provider = "google";
            logProvider("provider_replaced", {
                uid: firebaseUser.uid,
                fromProvider: profile.provider,
                toProvider: "google",
            });
        }

        // Always sync photoURL from Google so it stays current and fills in
        // any profiles that were created before photoURL was captured.
        if (firebaseUser.photoURL && firebaseUser.photoURL !== profile.photoURL) {
            updates.photoURL = firebaseUser.photoURL;
            logProvider("photo_synced", {
                uid: firebaseUser.uid,
                hadPhoto: !!profile.photoURL,
            });
        }

        if (!profileFullName && googleDisplayName) {
            updates.fullName = googleDisplayName;
            updates.firstName = deriveFirstName(googleDisplayName);
            logProvider("full_name_synced", {
                uid: firebaseUser.uid,
            });
        }

        if (profileFullName && !normalizeName(profile.firstName || '')) {
            updates.firstName = deriveFirstName(profileFullName);
        }

        await updateDoc(userRef, updates);

        const needsOnboarding = !profileFullName && !googleDisplayName;
        if (needsOnboarding) {
            logProvider("onboarding_required_missing_name", {
                uid: firebaseUser.uid,
            });
        }

        return { needsOnboarding };
    }

    logProvider("onboarding_required", {
        uid: firebaseUser.uid,
        email: firebaseUser.email || null,
    });

    return { needsOnboarding: true };
};

export const backfillOnboardingCompleted = async (userId) => {
    if (!userId) return;
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
        onboardingCompleted: true,
        updatedAt: serverTimestamp(),
    });
};
